import { computed, type ComputedRef, type Ref } from 'vue';
import type { ticketDetails } from 'shared';
import type { flightEntity } from 'freedom-shared';

import {
  directFlightsLib,
  type directFlightsTypes,
} from '@/widgets/direct-flights';

import {
  computeFirstDirectionTimeItems,
  computeSecondDirectionTimeItems,
} from '../lib';
import type { TimeItem } from '../types';

export function useTimeSelection(
  filteredOffersByAirline: ComputedRef<flightEntity.types.MappedOffer[]>,
  activeOffer: Ref<ticketDetails.types.MappedOffer | undefined>,
  isRoundTrip: ComputedRef<boolean>,
  backwardTimesMapping: ComputedRef<
    directFlightsTypes.BackwardTimesMapping | undefined
  >,
  forwardTimesMapping: ComputedRef<
    directFlightsTypes.ForwardTimesMapping | undefined
  >,
) {
  const firstDirectionTimeItems = computed<TimeItem[]>(() => {
    return computeFirstDirectionTimeItems(
      filteredOffersByAirline.value,
      activeOffer.value,
      isRoundTrip.value,
      forwardTimesMapping.value,
    );
  });

  const secondDirectionTimeItems = computed<TimeItem[]>(() => {
    if (!isRoundTrip.value) {
      return [];
    }
    return computeSecondDirectionTimeItems(
      filteredOffersByAirline.value,
      activeOffer.value,
      backwardTimesMapping.value,
    );
  });

  const getDirectionTimeItems = (flightIdx: number): TimeItem[] => {
    if (flightIdx === 0) {
      return firstDirectionTimeItems.value;
    }
    if (flightIdx === 1 && isRoundTrip.value) {
      return secondDirectionTimeItems.value;
    }
    return [];
  };

  const findOfferByTime = (
    time: string,
    date: string,
    flightIndex: number,
  ): flightEntity.types.MappedOffer | null => {
    const matchingOffers = filteredOffersByAirline.value.filter(offer => {
      const { time: depTime, date: depDate } =
        directFlightsLib.getCurrentFlightTimeAndDate(offer, flightIndex);

      return depTime === time && depDate === date;
    });

    if (matchingOffers.length === 0) {
      return null;
    }

    // All matching offers have the same time for this flightIndex, so just sort by price
    const sorted = directFlightsLib.sortOffersByPrice(matchingOffers);
    return sorted[0];
  };

  const handleFirstDirectionTimeClick = (
    item: TimeItem,
    onOfferSelect: (offer: flightEntity.types.MappedOffer) => void,
  ) => {
    if (item.isDisabled) return;
    if (isRoundTrip.value) {
      // Get current backward time and date to preserve if possible
      const { time: currentBackwardTime, date: currentBackwardDate } =
        directFlightsLib.getCurrentFlightTimeAndDate(activeOffer.value, 1);

      // Find all offers with matching forward time
      const matchingOffers = filteredOffersByAirline.value.filter(offer => {
        const { time: firstTime, date: firstDate } =
          directFlightsLib.getCurrentFlightTimeAndDate(offer, 0);
        return firstTime === item.time && firstDate === item.date;
      });

      if (matchingOffers.length > 0) {
        // First, try to find offers with the new forward time AND same backward time
        if (currentBackwardTime && currentBackwardDate) {
          const offersWithSameBackwardTime = matchingOffers.filter(offer => {
            const { time: secondTime, date: secondDate } =
              directFlightsLib.getCurrentFlightTimeAndDate(offer, 1);
            return (
              secondTime === currentBackwardTime &&
              secondDate === currentBackwardDate
            );
          });

          if (offersWithSameBackwardTime.length > 0) {
            // All offers have the same forward time AND same backward time, so just sort by price
            const sorted = directFlightsLib.sortOffersByPrice(
              offersWithSameBackwardTime,
            );
            const bestOffer = sorted[0];
            onOfferSelect(bestOffer);
            return;
          }
        }

        // If no offers with same backward time, fall back to cheapest/earliest with new forward time
        // Since all offers have the same forward time, sort by backward time (earliest first)
        const sorted = directFlightsLib.sortOffersByPriceAndTime(
          matchingOffers,
          1,
        );
        const bestOffer = sorted[0];
        onOfferSelect(bestOffer);
      }
    } else {
      // For OW, just select the offer with matching time
      const offer = findOfferByTime(item.time, item.date, 0);
      if (offer) {
        onOfferSelect(offer);
      }
    }
  };

  const handleSecondDirectionTimeClick = (
    item: TimeItem,
    onOfferSelect: (offer: flightEntity.types.MappedOffer) => void,
  ) => {
    if (!isRoundTrip.value || !activeOffer.value || item.isDisabled) return;

    // Get current forward flight time and date to preserve it
    const { time: currentForwardTime, date: currentForwardDate } =
      directFlightsLib.getCurrentFlightTimeAndDate(activeOffer.value, 0);

    // Find offers with matching backward time AND matching forward time
    // This ensures the forward flight time doesn't change when selecting backward time
    const matchingOffers = filteredOffersByAirline.value.filter(offer => {
      const { time: firstTime, date: firstDate } =
        directFlightsLib.getCurrentFlightTimeAndDate(offer, 0);
      const { time: secondTime, date: secondDate } =
        directFlightsLib.getCurrentFlightTimeAndDate(offer, 1);

      return (
        firstTime === currentForwardTime &&
        firstDate === currentForwardDate &&
        secondTime === item.time &&
        secondDate === item.date
      );
    });

    if (matchingOffers.length > 0) {
      // All offers have the same forward time AND same backward time, so just sort by price
      const sorted = directFlightsLib.sortOffersByPrice(matchingOffers);
      const bestOffer = sorted[0];
      onOfferSelect(bestOffer);
    }
  };

  const handleDirectionTimeClick = (
    item: TimeItem,
    flightIdx: number,
    onOfferSelect: (offer: flightEntity.types.MappedOffer) => void,
  ) => {
    if (flightIdx === 0) {
      handleFirstDirectionTimeClick(item, onOfferSelect);
    } else if (flightIdx === 1 && isRoundTrip.value) {
      handleSecondDirectionTimeClick(item, onOfferSelect);
    }
  };

  /**
   * Applies selection when user confirms choosing a disabled time.
   * Selects the clicked time and sets other direction to minimal price + earliest time.
   */
  const applyDisabledTimeSelection = (
    item: TimeItem,
    flightIdx: number,
    onOfferSelect: (offer: flightEntity.types.MappedOffer) => void,
  ) => {
    if (!item.isDisabled) return;

    if (flightIdx === 0 && isRoundTrip.value) {
      const matchingOffers = filteredOffersByAirline.value.filter(offer => {
        const { time: firstTime, date: firstDate } =
          directFlightsLib.getCurrentFlightTimeAndDate(offer, 0);
        return firstTime === item.time && firstDate === item.date;
      });

      if (matchingOffers.length > 0) {
        const sorted = directFlightsLib.sortOffersByPriceAndTime(
          matchingOffers,
          1,
        );
        onOfferSelect(sorted[0]);
      }
    } else if (flightIdx === 1 && isRoundTrip.value) {
      const matchingOffers = filteredOffersByAirline.value.filter(offer => {
        const { time: secondTime, date: secondDate } =
          directFlightsLib.getCurrentFlightTimeAndDate(offer, 1);
        return secondTime === item.time && secondDate === item.date;
      });

      if (matchingOffers.length > 0) {
        const sorted = directFlightsLib.sortOffersByPriceAndTime(
          matchingOffers,
          0,
        );
        onOfferSelect(sorted[0]);
      }
    }
  };

  return {
    firstDirectionTimeItems,
    secondDirectionTimeItems,
    getDirectionTimeItems,
    handleDirectionTimeClick,
    applyDisabledTimeSelection,
  };
}
